import { Injectable, NgZone } from '@angular/core';
import {
  ConfettiAnimationOptions,
  ConfettiParticle,
  ConfettiParticleOptions,
  ConfettiParticleSetPositionOptions,
  ConfettiParticleRepositionOptions,
  ConfettiParticleStepOptions,
} from './confetti-dropper.model';

@Injectable({
  providedIn: 'root',
})
export class ConfettiDropperService {
  public static colorOptions = [
    'DodgerBlue',
    'OliveDrab',
    'Gold',
    'pink',
    'SlateBlue',
    'lightblue',
    'Violet',
    'PaleGreen',
    'SteelBlue',
    'SandyBrown',
    'Chocolate',
    'Crimson',
  ];

  public angle: number = 0;
  public animationComplete = true;
  public animationHandler: number; // reference to animation handler to clear
  public colorIndex: number = 0;
  public colorIncrementer: number = 0;
  public confettiActive = false;
  public tiltAngle: number = 0;

  constructor(private ngZone: NgZone) {}

  /**
   * Creates an individual confetti object-- should return an object
   *
   * @param opts
   *
   * @returns {ConfettiParticle} Particle generated with a random color and placement
   */
  public createConfettiParticle({
    maxParticles,
    colorIncrement,
    canvas,
  }: ConfettiParticleOptions): ConfettiParticle {
    return {
      xAxis: Math.random() * canvas.width,
      yAxis: Math.random() * canvas.height - canvas.height,
      radius: Math.floor(Math.random() * (30 - 10 + 1) + 10),
      density: Math.random() * maxParticles + 10,
      color: this.getColor(colorIncrement),
      tilt: Math.floor(Math.random() * 10) - 10,
      tiltAngleIncremental: Math.random() * 0.07 + 0.05,
      tiltAngle: 1,
    };
  }

  /**
   * Creates an array of particles equal to the value set in `maxParticles`
   *
   * @param opts
   *
   * @returns {ConfettiParticle[]} A list of generated confetti particles
   */
  public createParticleList(opts: ConfettiParticleOptions): ConfettiParticle[] {
    const particles = [];

    this.animationComplete = false;

    for (let i = 0; i < opts.maxParticles; i++) {
      particles.push(this.createConfettiParticle(opts));
    }

    return particles;
  }

  /**
   * Each particle will use this function to draw out the individual strokes
   * using the context and a particle reference.
   *
   * @param {CanvasRenderContext2D} context Context of the canvas from HTMLCanvasElement.getContext
   * @param {ConfettiParticle} particle Particle options to use to modify the stroke
   *
   * @returns {CanvasRenderContext2D} The context after drawing changes
   */
  public drawParticle(
    context: CanvasRenderingContext2D,
    particle: ConfettiParticle
  ): CanvasRenderingContext2D {
    const { xAxis, yAxis, radius, tilt, color } = particle;

    context.beginPath();
    context.lineWidth = radius / 2;
    context.strokeStyle = color;
    context.moveTo(xAxis + tilt + radius / 4, yAxis);
    context.lineTo(xAxis + tilt, yAxis + tilt + radius / 4);
    context.stroke();

    return context;
  }

  /**
   * Clears the canvas area and calls `drawParticle()` on each individual particle
   *
   * @returns {CanvasRenderingContext2D[]} The list of references to individual particle draws
   */
  public drawCanvas(
    context: CanvasRenderingContext2D,
    particles: ConfettiParticle[]
  ): CanvasRenderingContext2D {
    // clear canvas in preparation for redraw
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);

    particles.map((particle) => {
      // draws the new particle positions
      this.drawParticle(context, particle);
    });

    this.updateCanvas(particles, {
      canvas: context.canvas,
      context,
    });

    return context;
  }

  /**
   * Redraw each particle in a new position or stop the animation gradually
   * or immediately.
   *
   * @returns {void}
   */
  public updateCanvas(
    particles: ConfettiParticle[],
    opts: ConfettiAnimationOptions
  ): void {
    // changes angle and tiltAngle every time we call updateCanvas
    this.angle += 0.01;
    this.tiltAngle += 0.1;

    // local counter
    let remainingFlakes: number = 0;

    for (let i = 0; i < particles.length; i++) {
      const particle = particles[i];

      // If stopConfetti was called and set this, don't
      // continue to animate the falling pieces, breaks out of loop
      if (this.animationComplete) {
        return;
      }

      // If the confetti has been deactivated and the particle
      // is not yet in the drawing area (above the top), continue to move it
      // down the screen; skip the rest of the actions on the particle
      // and go to the next one
      if (!this.confettiActive && particle.yAxis < -15) {
        particle.yAxis = opts.canvas.height + 100;
        break;
      }

      this.stepParticle(particle, { angle: this.angle, particleIndex: i });

      // When the particle is still above the bottom of the canvas,
      // add it to the 'remainingFlakes' counter
      if (particle.yAxis <= opts.canvas.height) {
        remainingFlakes++;
      }

      this.repositionParticle(particle, {
        angle: this.angle,
        particleIndex: i,
        canvas: opts.canvas,
      });
    }

    if (remainingFlakes === 0) {
      this.stopConfetti(opts.context);
    }
  }

  /**
   * Kicks off an infinite animation loop that will end when a global variable is set to true.
   *
   * @param {CanvasRenderingContext2D} context Canvas context to draw on
   * @param {ConfettiParticle[]} particles List of particles to draw on the canvas
   * @returns {void}
   */
  public startConfetti(
    context: CanvasRenderingContext2D,
    particles: ConfettiParticle[]
  ): void {
    this.confettiActive = true;
    this.animationComplete = false;

    // run the canvas drawing outside of angular so we don't trigger unnecessary angular updates
    this.ngZone.runOutsideAngular(() => {
      this.loopAnimation(context, particles);
    });
  }

  /**
   * Uses window.requestAnimationFrame to draw the frames of the animation until
   * animation is stopped with `animationComplete`.
   *
   * @param {CanvasRenderingContext2D} context Canvas context to draw on
   * @param {ConfettiParticle[]} particles List of particles to draw on the canvas
   * @returns {void}
   */
  public loopAnimation(
    context: CanvasRenderingContext2D,
    particles: ConfettiParticle[]
  ) {
    if (this.animationComplete) {
      return null;
    }
    this.drawCanvas(context, particles);
    this.animationHandler = window.requestAnimationFrame(() =>
      this.loopAnimation(context, particles)
    );
  }

  /**
   * Currently only being used to stop the confetti after a particular timeout.
   * Causes a gradual stop where new confetti is not created but the remaining confetti
   * will continue to fall until they fall out of frame.
   *
   * @returns {void}
   */
  public deactivateConfetti(): void {
    this.confettiActive = false;
    clearTimeout(this.animationHandler);
  }

  /**
   * Immediately stops the confetti view and clears the canvas; will get called after a
   * `ConfettiDropperService.deactivateConfetti` call once all flakes have fallen off to
   * clear the canvas and clean up.
   *
   * @returns {void}
   */
  public stopConfetti(context: CanvasRenderingContext2D): void {
    this.animationComplete = true; // kill the animation loop
    // and then clear the canvas
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  }

  /**
   * Returns a color from the colorOptions list in a pseudorandom manner.
   *
   * @returns {string} One of the valid colorOptions
   */
  public getColor(colorIncrement: number): string {
    if (this.colorIncrementer >= colorIncrement) {
      this.colorIncrementer = 0;
      this.colorIndex++;
      if (this.colorIndex >= ConfettiDropperService.colorOptions.length) {
        this.colorIndex = 0;
      }
    }
    this.colorIncrementer++;
    return ConfettiDropperService.colorOptions[this.colorIndex];
  }

  /**
   * Gets a new randomized size value for the confetti radius to display
   * smaller or larger pieces of confetti.
   *
   * @param min The smallest confetti size
   * @param max The largest confetti size
   * @returns {number} Randomized number between the two given numbers
   */
  public getRandomSize(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  /**
   * Takes a particle and determins if it is active and outside of the bounds of the
   * drawing area, it will give it a new position within the frame.
   *
   * Uses some characteristics to randomize starting location and always randomize the tilt.
   * @returns {void}
   */
  public repositionParticle(
    particle: ConfettiParticle,
    opts: ConfettiParticleRepositionOptions
  ): void {
    const { canvas, particleIndex, angle } = opts;
    const isParticleOffLeftSide = particle.xAxis < -20;
    const isParticleOffRightSide = particle.xAxis > canvas.width + 20;
    const isParticleOffBottom = particle.yAxis > canvas.height;
    const isParticleOutOfBounds =
      isParticleOffLeftSide || isParticleOffRightSide || isParticleOffBottom;

    if (isParticleOutOfBounds && this.confettiActive) {
      const tilt = Math.floor(Math.random() * 10) - 20;
      const isIndexEven = particleIndex % 2 === 0;
      const isIndexNotDivisibleBy5 = particleIndex % 5 > 0;
      let xCoordinate = 0,
        yCoordinate = 0;

      // The conditionals are meant to just pseudorandomize
      // the movement of the confetti pieces so they fall
      // more naturally down the page, some going left or right
      // they all drop at the same speed and twirl randomly.
      if (isIndexNotDivisibleBy5 || isIndexEven) {
        xCoordinate = Math.random() * canvas.width;
        yCoordinate = -10;
      } else if (Math.sin(angle) > 0) {
        xCoordinate = -20;
        yCoordinate = Math.random() * canvas.height;
      } else {
        xCoordinate = canvas.width + 20;
        yCoordinate = Math.random() * canvas.height;
      }

      this.setParticlePosition(particle, {
        yCoordinate,
        xCoordinate,
        tilt,
      });
    }
  }

  /**
   * Utility function to reset the particle position and tilt
   *
   * @returns {void}
   */
  public setParticlePosition(
    particle: ConfettiParticle,
    { xCoordinate, yCoordinate, tilt }: ConfettiParticleSetPositionOptions
  ): void {
    particle.xAxis = xCoordinate;
    particle.yAxis = yCoordinate;
    particle.tilt = tilt;
  }

  /**
   * Move the particle along the canvas using some randomization
   * and initial config to determine the new location and tilt
   *
   * @param {ConfettiParticle} particle
   * @param opts
   * @returns {void}
   */
  public stepParticle(
    particle: ConfettiParticle,
    opts: ConfettiParticleStepOptions
  ): void {
    const { particleIndex, angle } = opts;
    particle.tiltAngle += particle.tiltAngleIncremental;
    const yCoordinate =
      particle.yAxis +
      (Math.cos(angle + particle.density) + 3 + particle.radius / 2) / 2;
    const xCoordinate = particle.xAxis + Math.sin(angle);
    const tilt = Math.sin(particle.tiltAngle - particleIndex / 3) * 15;

    this.setParticlePosition(particle, {
      yCoordinate,
      xCoordinate,
      tilt,
    });
  }
}
